import React, { useState, useEffect, useRef, useContext } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { SendOutlined, SmileOutlined } from '@ant-design/icons';
import { Drawer, Tooltip } from "antd";
import chatApi from '../../services/api/chatAPI';
import { NotificationContext } from '../../contexts/notificationContext';
import './chat.css';
import useChatStore from '../../hooks/useChatStore';
import { useBranding } from '../../contexts/brandingContext';
const ChatBox = () => {
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const { unReadMessages, fetchChatHistory } = useContext(NotificationContext);
    const { isChatOpen, toggleChat, setChatOpen, hasChatInitialized } = useChatStore();
    const { brandName } = useBranding();
    // Fetch messages and mark as read when chat opens
    useEffect(() => {
        if (isChatOpen) {
            fetchChatHistory();
            markMessagesAsRead();
        }
    }, [isChatOpen]);

    useEffect(() => {
        if (!hasChatInitialized) {
            setChatOpen(false);
        }
        fetchChatHistory();
    }, []);

    // Mark messages as read
    const markMessagesAsRead = async () => {
        try {
            const response = await chatApi.UpdateChat();
            if (response && response.status)
                fetchChatHistory();
            console.log("Messages marked as read.");
        } catch (error) {
            console.log("Error marking messages as read:", error);
        }
    };

    // Handle sending messages
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (newMessage.trim() === '') return;

        try {
            await chatApi.CreateChat({ senderType: 'customer', message: newMessage });
            setNewMessage('');
            fetchChatHistory(); // Refresh chat after sending message
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    // Scroll to the latest message
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [unReadMessages]);

    return (
        <>
            {/* Chat Icon with Unread Count */}
            {!isChatOpen && (
                <Tooltip title="Chat">
                    <div style={{ position: "relative" }}>
                        {unReadMessages.length > 0 && (
                            <div className="sub-circle position-absolute">
                                {unReadMessages.filter(n => !n.isRead && n.sender === "agent").length}
                            </div>
                        )}
                        <i className="fa-brands fa-rocketchat icon-blue"
                            style={{ fontSize: "32px", cursor: "pointer" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                toggleChat();
                            }}>
                        </i>
                    </div>
                </Tooltip>
            )}

            {/* Chat Drawer */}
            <Drawer
                title={`Chat with ${brandName}`}
                className="chat-box-drawer"
                placement="right"
                closable={true}
                open={isChatOpen}
                onClose={() => setChatOpen(false)}
                maskClosable={false}
                destroyOnClose={true}
                style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
            >
                <div className="chat-container">
                    {/* Messages List */}
                    <div className="chat-messages">
                        {unReadMessages.length === 0 ? (
                            <div className="no-messages">No messages yet. Start a conversation!</div>
                        ) : (
                            unReadMessages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`message ${message.sender === 'user' ? 'user-message' : 'agent-message'}`}
                                >
                                    <div className="message-content">{message.text}</div>
                                    {message.timestamp && (
                                        <div className="message-time">
                                            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Chat Input */}
                    <div className="chat-input-container">
                        <form className="chat-input-form" onSubmit={handleSendMessage}>
                            <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="emoji-button">
                                <SmileOutlined />
                            </button>

                            {showEmojiPicker && (
                                <div className="emoji-picker">
                                    <EmojiPicker
                                        onEmojiClick={(emoji) => setNewMessage(prev => prev + emoji.emoji)}
                                    />
                                </div>
                            )}

                            <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type your message..."
                                className="chat-input"
                            />
                            <button type="submit" className="send-button">
                                <SendOutlined />
                            </button>
                        </form>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default ChatBox;
